import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import {
  fetchShipments,
  fetchShipment,
  createShipment,
  updateShipment,
  updateShipmentStatus,
  syncShipmentTracking,
  deleteShipment,
  selectShipments,
  selectCurrentShipment,
  selectShipmentPagination,
  selectShipmentsLoading,
  selectShipmentsError,
  clearCurrent,
} from "../redux/slices/shipmentSlice";

const useShipments = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const shipments = useSelector(selectShipments);
  const current = useSelector(selectCurrentShipment);
  const pagination = useSelector(selectShipmentPagination);
  const loading = useSelector(selectShipmentsLoading);
  const error = useSelector(selectShipmentsError);

  const load = useCallback(
    (params) => dispatch(fetchShipments(params)),
    [dispatch],
  );
  const loadOne = useCallback((id) => dispatch(fetchShipment(id)), [dispatch]);

  const create = useCallback(
    async (data) => {
      const result = await dispatch(createShipment(data));
      if (createShipment.fulfilled.match(result)) {
        // No generic "created" snackbar here — the caller shows a proper
        // success dialog (ShipmentCreatedDialog) with a tracking number and
        // a "Create Another" action, which would otherwise double up with
        // this.
        // A promoCode was submitted — let the customer know whether it
        // actually redeemed (ungated) or is still pending confirmation
        // (size-gated, resolved once the warehouse weighs the package
        // in). A failed/expired code just gets its own message, same
        // toast pattern, never blocking the shipment itself.
        const promo = result.payload.promo;
        if (promo?.message) {
          enqueueSnackbar(promo.message, { variant: promo.applied ? "success" : "info" });
        }
        return result.payload.shipment;
      }
      enqueueSnackbar(result.payload || "Failed to create shipment", {
        variant: "error",
      });
      return null;
    },
    [dispatch, enqueueSnackbar],
  );

  const update = useCallback(
    async (id, data) => {
      const result = await dispatch(updateShipment({ id, updates: data }));
      if (updateShipment.fulfilled.match(result)) {
        enqueueSnackbar("Shipment updated", { variant: "success" });
        return result.payload;
      }
      enqueueSnackbar(result.payload || "Failed to update", {
        variant: "error",
      });
      return null;
    },
    [dispatch, enqueueSnackbar],
  );

  const changeStatus = useCallback(
    async (id, status, note, notifyCustomer = true) => {
      const result = await dispatch(
        updateShipmentStatus({ id, status, note, notifyCustomer }),
      );
      if (updateShipmentStatus.fulfilled.match(result)) {
        enqueueSnackbar("Status updated", { variant: "success" });
        return result.payload;
      }
      enqueueSnackbar(result.payload || "Failed to update status", {
        variant: "error",
      });
      return null;
    },
    [dispatch, enqueueSnackbar],
  );

  const remove = useCallback(
    async (id) => {
      const result = await dispatch(deleteShipment(id));
      if (deleteShipment.fulfilled.match(result)) {
        enqueueSnackbar("Shipment deleted", { variant: "success" });
        return true;
      }
      enqueueSnackbar(result.payload || "Failed to delete", {
        variant: "error",
      });
      return false;
    },
    [dispatch, enqueueSnackbar],
  );

  const syncTracking = useCallback(
    async (id) => {
      const result = await dispatch(syncShipmentTracking(id));
      if (syncShipmentTracking.fulfilled.match(result)) {
        enqueueSnackbar("Tracking synchronized", { variant: "success" });
        return result.payload;
      }
      enqueueSnackbar(result.payload || "Failed to synchronize tracking", {
        variant: "error",
      });
      return null;
    },
    [dispatch, enqueueSnackbar],
  );

  const clear = useCallback(() => dispatch(clearCurrent()), [dispatch]);

  return {
    shipments,
    current,
    pagination,
    loading,
    error,
    load,
    loadOne,
    create,
    update,
    changeStatus,
    remove,
    syncTracking,
    clear,
  };
};

export default useShipments;
